// components/ui/Select.jsx - Simplified Select without Portal
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { cn } from "../../utils/cn";


const Select = React.forwardRef(({
    className,
    options = [],
    value,
    defaultValue,
    placeholder = "Sélectionner...",
    multiple = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    searchable = false,
    clearable = false,
    loading = false,
    id,
    name,
    onChange,
    onOpenChange,
    ...props
}, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Generate unique ID if not provided
    const selectId = id || `select-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Calculate dropdown position
    useEffect(() => {
        if (isOpen && triggerRef?.current) {
            const rect = triggerRef?.current?.getBoundingClientRect();
            setDropdownPosition({
                top: rect?.bottom + window.scrollY + 8,
                left: rect?.left + window.scrollX,
                width: rect?.width
            });
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef?.current && !containerRef?.current?.contains(event?.target) &&
                dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
                setIsOpen(false);
                onOpenChange?.(false);
                setSearchTerm("");
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('touchstart', handleClickOutside);
            };
        }
    }, [isOpen, onOpenChange]);

    // Prevent body scroll when dropdown is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Filter options based on search
    const filteredOptions = searchable && searchTerm
        ? options?.filter(option =>
            option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
            (option?.value && option?.value?.toString()?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
        )
        : options;

    // Get selected option(s) for display
    const getSelectedDisplay = () => {
        if (!value) return placeholder;

        if (multiple) {
            const selectedOptions = options?.filter(opt => value?.includes(opt?.value));
            if (selectedOptions?.length === 0) return placeholder;
            if (selectedOptions?.length === 1) return selectedOptions?.[0]?.label;
            return `${selectedOptions?.length} éléments sélectionnés`;
        }

        const selectedOption = options?.find(opt => opt?.value === value);
        return selectedOption ? selectedOption?.label : placeholder;
    };

    const handleToggle = (e) => {
        e?.preventDefault();
        if (!disabled) {
            const newIsOpen = !isOpen;
            setIsOpen(newIsOpen);
            onOpenChange?.(newIsOpen);
            if (!newIsOpen) {
                setSearchTerm("");
            }
        }
    };

    const handleOptionSelect = (option) => {
        if (multiple) {
            const newValue = value || [];
            const updatedValue = newValue?.includes(option?.value)
                ? newValue?.filter(v => v !== option?.value)
                : [...newValue, option?.value];
            onChange?.(updatedValue);
        } else {
            onChange?.(option?.value);
            setIsOpen(false);
            onOpenChange?.(false);
            setSearchTerm("");
        }
    };

    const handleClear = (e) => {
        e?.stopPropagation();
        e?.preventDefault();
        onChange?.(multiple ? [] : '');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e?.target?.value);
    };

    const isSelected = (optionValue) => {
        if (multiple) {
            return value?.includes(optionValue) || false;
        }
        return value === optionValue;
    };

    const hasValue = multiple ? value?.length > 0 : value !== undefined && value !== '';

    const dropdownContent = isOpen && (
        <>
            {/* Backdrop overlay */}
            <div 
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
                onClick={() => {
                    setIsOpen(false);
                    onOpenChange?.(false);
                    setSearchTerm("");
                }}
            />
            
            {/* Dropdown menu */}
            <div 
                ref={dropdownRef}
                className="fixed z-[9999]"
                style={{
                    top: `${dropdownPosition?.top}px`,
                    left: `${dropdownPosition?.left}px`,
                    width: `${dropdownPosition?.width}px`
                }}
            >
                <div className="bg-[rgba(18,18,22,0.95)] backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden">
                    {searchable && (
                        <div className="search-input-wrapper">
                            <Search className="search-icon" size={16} />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="search-input"
                                placeholder="Rechercher..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e?.target?.value)}
                                onClick={(e) => e?.stopPropagation()}
                            />
                        </div>
                    )}

                    <div className="max-h-64 overflow-y-auto py-2">
                        {filteredOptions?.length === 0 && (
                            <div className="no-options">
                                Aucune option trouvée
                            </div>
                        )}
                        {filteredOptions?.map((option) => (
                            <button
                                key={option?.value}
                                type="button"
                                className={cn(
                                    "w-full px-4 py-3 text-left flex items-center justify-between gap-2",
                                    "transition-all duration-200",
                                    "hover:bg-primary/20",
                                    "text-base",
                                    isSelected(option?.value) && "bg-primary/30 text-white font-medium",
                                    !isSelected(option?.value) && "text-gray-200",
                                    option?.disabled && "opacity-50 cursor-not-allowed"
                                )}
                                onClick={() => !option?.disabled && handleOptionSelect(option)}
                                onTouchEnd={(e) => {
                                    e?.preventDefault();
                                    !option?.disabled && handleOptionSelect(option);
                                }}
                                disabled={option?.disabled}
                            >
                                <span className="flex-1 truncate text-base">{option?.label}</span>
                                {multiple && isSelected(option?.value) && (
                                    <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                                )}
                                {option?.description && (
                                    <span className="text-xs text-gray-400 flex-shrink-0">
                                        {option?.description}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div ref={containerRef} className={cn("relative w-full", className)}>
            {label && (
                <label
                    htmlFor={selectId}
                    className={cn(
                        "block text-sm font-medium mb-2",
                        error ? "text-red-400" : "text-gray-200"
                    )}
                >
                    {label}
                    {required && <span className="text-red-400 ml-1">*</span>}
                </label>
            )}
            
            <div className="relative">
                {/* Trigger button */}
                <button
                    ref={triggerRef}
                    id={selectId}
                    type="button"
                    className={cn(
                        "w-full h-12 px-4 rounded-2xl bg-[rgba(245,245,247,0.95)] border text-[#1A1A1A] text-left",
                        "flex items-center justify-between gap-2",
                        "transition-all duration-300",
                        "hover:border-primary/60 hover:shadow-[0_0_20px_rgba(74,144,226,0.15)]",
                        "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60",
                        "text-base font-medium",
                        error ? "border-red-400/60" : "border-white/20",
                        disabled && "opacity-50 cursor-not-allowed",
                        !hasValue && "text-[#8E8E93]"
                    )}
                    onClick={handleToggle}
                    onTouchEnd={handleToggle}
                    disabled={disabled}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    style={{ fontSize: '16px' }}
                    {...props}
                >
                    <span className="truncate flex-1 text-base">{getSelectedDisplay()}</span>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {loading && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        )}

                        {clearable && hasValue && !loading && (
                            <button
                                type="button"
                                className="p-0.5 hover:bg-black/10 rounded transition-colors"
                                onClick={handleClear}
                                onTouchEnd={handleClear}
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}

                        <ChevronDown className={cn(
                            "h-4 w-4 transition-transform duration-200",
                            isOpen && "rotate-180"
                        )} />
                    </div>
                </button>

                {/* Hidden native select for form submission */}
                <select
                    name={name}
                    value={value || ''}
                    onChange={() => { }} // Controlled by our custom logic
                    className="sr-only"
                    tabIndex={-1}
                    multiple={multiple}
                    required={required}
                >
                    <option value="">Select...</option>
                    {options?.map(option => (
                        <option key={option?.value} value={option?.value}>
                            {option?.label}
                        </option>
                    ))}
                </select>

                {description && !error && (
                    <p className="text-sm text-muted-foreground mt-2">
                        {description}
                    </p>
                )}

                {error && (
                    <p className="text-sm text-red-400 mt-2">
                        {error}
                    </p>
                )}
            </div>

            {/* Render dropdown via portal to body */}
            {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
        </div>
    );
});

Select.displayName = "Select";

export default Select;